import React, { useState, useEffect, useMemo, useCallback } from "react";
import { motion } from "framer-motion";
import { Lock, Loader2, LogOut, Search, Download, Users, MessageSquare, X, Bot } from "lucide-react";
import { toast } from "sonner";
import Seo from "@/components/Seo";
import { API, api, adminApi, formatApiErrorDetail } from "@/lib/api";
import { normalizeBrand } from "@/lib/brand";
import { LOGO_WHITE } from "@/data/content";

const TOKEN_KEY = "eram_admin_token";

function Login({ onAuth }) {
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");

  const submit = async (e) => {
    e.preventDefault();
    setLoading(true);
    setError("");
    try {
      const { data } = await api.post("/admin/login", { email, password });
      localStorage.setItem(TOKEN_KEY, data.token);
      onAuth(data.token);
    } catch (err) {
      setError(formatApiErrorDetail(err.response?.data?.detail) || "Login failed.");
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="flex min-h-screen items-center justify-center bg-[#262A30] px-4 py-12">
      <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} className="w-full max-w-md">
        <img src={LOGO_WHITE} alt="ERAM DIGITAL" width="421" height="183" className="mb-8 h-14 w-auto" data-testid="admin-login-logo" />
        <div className="gradient-border rounded-3xl p-8 sm:p-10">
          <div className="mb-6 flex items-center gap-2 text-[#4B5563]"><Lock className="h-4 w-4" /><span className="text-sm">Admin access</span></div>
          <h1 className="font-display text-2xl font-semibold text-[#0A0A0A]">Sign in to dashboard</h1>
          <form onSubmit={submit} className="mt-6 space-y-4" data-testid="admin-login-form">
            <input type="email" value={email} onChange={(e) => setEmail(e.target.value)} placeholder="Email" required className="w-full rounded-xl border border-[#E5E7EB] bg-[#F3F4F6] px-4 py-3 text-sm text-[#0A0A0A] placeholder:text-[#6B7280] focus:border-brand-violet focus:outline-none" data-testid="admin-email" />
            <input type="password" value={password} onChange={(e) => setPassword(e.target.value)} placeholder="Password" required className="w-full rounded-xl border border-[#E5E7EB] bg-[#F3F4F6] px-4 py-3 text-sm text-[#0A0A0A] placeholder:text-[#6B7280] focus:border-brand-violet focus:outline-none" data-testid="admin-password" />
            {error && <p className="text-sm text-destructive" data-testid="admin-error">{error}</p>}
            <button type="submit" disabled={loading} className="flex w-full items-center justify-center gap-2 rounded-xl gradient-bg py-3 text-sm font-semibold text-white disabled:opacity-60" data-testid="admin-login-submit">
              {loading ? <Loader2 className="h-4 w-4 animate-spin" /> : "Sign in"}
            </button>
          </form>
        </div>
      </motion.div>
    </div>
  );
}

function Dashboard({ token, onLogout }) {
  const [tab, setTab] = useState("leads");
  const [leads, setLeads] = useState([]);
  const [transcripts, setTranscripts] = useState([]);
  const [query, setQuery] = useState("");
  const [sourceFilter, setSourceFilter] = useState("all");
  const [loading, setLoading] = useState(true);
  const [selected, setSelected] = useState(null);

  const client = useMemo(() => adminApi(token), [token]);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const [l, tr] = await Promise.all([client.get("/leads"), client.get("/chat/transcripts")]);
      setLeads(l.data);
      setTranscripts(tr.data);
    } catch (err) {
      if (err.response?.status === 401) {
        toast.error("Session expired. Please sign in again.");
        onLogout();
      } else {
        toast.error("Failed to load data.");
      }
    } finally {
      setLoading(false);
    }
  }, [client, onLogout]);


  useEffect(() => {
    if (!selected) return;
    const closeOnEscape = (event) => { if (event.key === "Escape") setSelected(null); };
    window.addEventListener("keydown", closeOnEscape);
    return () => window.removeEventListener("keydown", closeOnEscape);
  }, [selected]);

  useEffect(() => { load(); }, [load]);

  const filteredLeads = useMemo(() => {
    return leads.filter((l) => {
      const matchesQuery = [l.name, l.email, l.company, l.message, l.service_interest]
        .filter(Boolean).join(" ").toLowerCase().includes(query.toLowerCase());
      const matchesSource = sourceFilter === "all" || l.source === sourceFilter;
      return matchesQuery && matchesSource;
    });
  }, [leads, query, sourceFilter]);

  const filteredTranscripts = useMemo(() => {
    return transcripts.filter((tr) =>
      JSON.stringify(tr.messages || []).toLowerCase().includes(query.toLowerCase())
    );
  }, [transcripts, query]);

  const exportCsv = () => {
    window.open(`${API}/leads/export?token=${encodeURIComponent(token)}`, "_blank");
  };

  return (
    <div className="min-h-screen">
      <header className="sticky top-0 z-40 border-b border-white/10 bg-[#262A30]">
        <div className="mx-auto flex max-w-7xl items-center justify-between px-4 py-3 sm:px-6">
          <div className="flex items-center gap-3">
            <img src={LOGO_WHITE} alt="ERAM DIGITAL" width="421" height="183" className="h-10 w-auto" data-testid="admin-header-logo" />
            <span className="text-sm font-medium text-slate-300">Admin</span>
          </div>
          <button onClick={onLogout} className="flex items-center gap-2 rounded-full border border-white/20 px-4 py-2 text-sm text-white hover:bg-white/10" data-testid="admin-logout">
            <LogOut className="h-4 w-4" />Sign out
          </button>
        </div>
      </header>

      <div className="mx-auto max-w-7xl px-4 py-8 sm:px-6">
        <div className="mb-6 grid grid-cols-2 gap-4 sm:grid-cols-4">
          <StatCard icon={Users} label="Total leads" value={leads.length} />
          <StatCard icon={MessageSquare} label="Conversations" value={transcripts.length} />
          <StatCard icon={Bot} label="Chat leads" value={leads.filter((l) => l.source === "chat").length} />
          <StatCard icon={Users} label="Contact leads" value={leads.filter((l) => l.source === "contact").length} />
        </div>

        <div className="mb-5 flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
          <div className="flex gap-2">
            <TabBtn active={tab === "leads"} onClick={() => setTab("leads")} testid="tab-leads">Leads ({filteredLeads.length})</TabBtn>
            <TabBtn active={tab === "transcripts"} onClick={() => setTab("transcripts")} testid="tab-transcripts">Transcripts ({filteredTranscripts.length})</TabBtn>
          </div>
          <div className="flex gap-2">
            <div className="relative flex-1 sm:w-64">
              <Search className="pointer-events-none absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-[#6B7280]" />
              <input value={query} onChange={(e) => setQuery(e.target.value)} placeholder="Search…" className="w-full rounded-xl border border-[#E5E7EB] bg-[#F3F4F6] py-2.5 pl-9 pr-3 text-sm text-[#0A0A0A] placeholder:text-[#6B7280] focus:border-brand-violet focus:outline-none" data-testid="admin-search" />
            </div>
            {tab === "leads" && (
              <>
                <select value={sourceFilter} onChange={(e) => setSourceFilter(e.target.value)} className="rounded-xl border border-[#E5E7EB] bg-[#F3F4F6] px-3 py-2.5 text-sm text-[#0A0A0A] focus:border-brand-violet focus:outline-none" data-testid="admin-source-filter">
                  <option value="all" className="bg-white">All sources</option>
                  <option value="contact" className="bg-white">Contact</option>
                  <option value="chat" className="bg-white">Chat</option>
                </select>
                <button onClick={exportCsv} className="flex items-center gap-2 rounded-xl gradient-bg px-4 py-2.5 text-sm font-semibold text-white" data-testid="admin-export-csv"><Download className="h-4 w-4" />CSV</button>
              </>
            )}
          </div>
        </div>

        {loading ? (
          <div className="flex h-64 items-center justify-center"><Loader2 className="h-6 w-6 animate-spin text-brand-violet" /></div>
        ) : tab === "leads" ? (
          <div className="overflow-x-auto rounded-2xl border border-[#E5E7EB]">
            <table className="w-full text-sm" data-testid="leads-table">
              <thead>
                <tr className="border-b border-[#E5E7EB] bg-[#F3F4F6] text-left text-xs uppercase tracking-widest text-[#6B7280]">
                  <th className="px-4 py-3">Name</th><th className="px-4 py-3">Email</th><th className="px-4 py-3">Company</th><th className="px-4 py-3">Interest</th><th className="px-4 py-3">Source</th><th className="px-4 py-3">Date</th>
                </tr>
              </thead>
              <tbody>
                {filteredLeads.length === 0 ? (
                  <tr><td colSpan={6} className="px-4 py-10 text-center text-[#6B7280]">No leads yet.</td></tr>
                ) : filteredLeads.map((l) => (
                  <tr key={l.id} className="border-b border-[#EEF0F2] hover:bg-[#F9FAFB]" data-testid={`lead-row-${l.id}`}>
                    <td className="px-4 py-3 text-[#0A0A0A]">{l.name}</td>
                    <td className="px-4 py-3 text-[#4B5563]">{l.email}</td>
                    <td className="px-4 py-3 text-[#4B5563]">{l.company || "—"}</td>
                    <td className="px-4 py-3 text-[#4B5563]">{l.service_interest || "—"}</td>
                    <td className="px-4 py-3"><span className={`rounded-full px-2.5 py-0.5 text-xs ${l.source === "chat" ? "bg-brand-teal/15 text-brand-teal" : "bg-brand-violet/15 text-brand-violet"}`}>{l.source}</span></td>
                    <td className="px-4 py-3 text-[#6B7280]">{new Date(l.created_at).toLocaleDateString()}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        ) : (
          <div className="grid gap-4 md:grid-cols-2 lg:grid-cols-3">
            {filteredTranscripts.length === 0 ? (
              <p className="col-span-full py-10 text-center text-[#6B7280]">No conversations yet.</p>
            ) : filteredTranscripts.map((tr) => (
              <button key={tr.session_id} onClick={() => setSelected(tr)} className="rounded-2xl border border-[#E5E7EB] bg-white p-5 text-left transition-colors hover:border-brand-violet/40" data-testid={`transcript-${tr.session_id}`}>
                <div className="flex items-center justify-between">
                  <span className="text-xs font-medium text-[#6B7280]">{(tr.messages || []).length} messages</span>
                  {tr.lead_captured && <span className="rounded-full bg-brand-teal/15 px-2 py-0.5 text-xs text-brand-teal">Lead</span>}
                </div>
                <p className="mt-3 line-clamp-3 text-sm text-[#4B5563]">{normalizeBrand((tr.messages || []).find((m) => m.role === "user")?.content || "—")}</p>
                <p className="mt-3 text-xs text-[#9CA3AF]">{new Date(tr.updated_at).toLocaleString()}</p>
              </button>
            ))}
          </div>
        )}
      </div>

      {selected && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/70 p-4" onClick={() => setSelected(null)}>
          <motion.div initial={{ opacity: 0, scale: 0.95 }} animate={{ opacity: 1, scale: 1 }} onClick={(e) => e.stopPropagation()} className="flex max-h-[80vh] w-full max-w-lg flex-col overflow-hidden rounded-3xl border border-[#E5E7EB] bg-white" data-testid="transcript-modal">
            <div className="flex items-center justify-between border-b border-[#E5E7EB] px-5 py-4">
              <span className="font-display text-sm font-semibold text-[#0A0A0A]">Conversation transcript</span>
              <button onClick={() => setSelected(null)} className="text-[#4B5563] hover:text-[#0A0A0A]" data-testid="transcript-close"><X className="h-5 w-5" /></button>
            </div>
            <div className="flex-1 space-y-3 overflow-y-auto p-5">
              {(selected.messages || []).map((m, i) => (
                <div key={i} className={`flex ${m.role === "user" ? "justify-end" : "justify-start"}`}>
                  <div className={`max-w-[85%] whitespace-pre-wrap rounded-2xl px-4 py-2.5 text-sm ${m.role === "user" ? "gradient-bg text-white" : "border border-[#E5E7EB] bg-[#F3F4F6] text-[#374151]"}`}>{normalizeBrand(m.content)}</div>
                </div>
              ))}
            </div>
          </motion.div>
        </div>
      )}
    </div>
  );
}

const StatCard = ({ icon: Icon, label, value }) => (
  <div className="rounded-2xl border border-[#E5E7EB] bg-white p-5">
    <Icon className="h-5 w-5 text-brand-violet" />
    <p className="mt-3 font-display text-3xl font-bold text-[#0A0A0A]">{value}</p>
    <p className="text-xs text-[#6B7280]">{label}</p>
  </div>
);

const TabBtn = ({ active, onClick, children, testid }) => (
  <button onClick={onClick} className={`rounded-full px-4 py-2 text-sm font-medium transition-colors ${active ? "gradient-bg text-white" : "border border-[#E5E7EB] text-[#4B5563] hover:text-[#0A0A0A]"}`} data-testid={testid}>{children}</button>
);

export default function Admin() {
  const [token, setToken] = useState(() => localStorage.getItem(TOKEN_KEY));

  const logout = () => {
    localStorage.removeItem(TOKEN_KEY);
    setToken(null);
  };

  return (
    <div className="min-h-screen bg-white">
      <Seo title="Admin Dashboard" description="ERAM DIGITAL admin dashboard." path="/admin" />
      {token ? <Dashboard token={token} onLogout={logout} /> : <Login onAuth={setToken} />}
    </div>
  );
}
